import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Jsona } from 'jsona';
import type { TJsonApiBody } from 'jsona/lib/JsonaTypes';

import { firstValueFrom, map, Observable, Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';

import { InyovaConfigService } from './inyova-config.service';
import { FeatureFlag, FeatureFlagEnabled } from '../../../models';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService implements OnDestroy {
  private initialize$: Observable<void> | null = null;
  protected readonly onDestroy$ = new Subject<void>();

  private jsona = new Jsona();
  private _featureFlags: Map<string, boolean> = new Map();

  private urlV3: string;
  private urlV4: string;

  constructor(
    private http: HttpClient,
    private sharedService: InyovaConfigService
  ) {
    this.sharedService
      .getApiData()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ urlV3, urlV4 }) => {
        this.urlV3 = urlV3;
        this.urlV4 = urlV4;
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  initialize(): Observable<void> {
    if (!this.initialize$) {
      this.initialize$ = this.getFeatureFlags().pipe(
        map((featureFlags) => {
          this._featureFlags = new Map(featureFlags.map(({ feature_key, value }) => [feature_key, value]));
        }),
        shareReplay(1)
      );
    }
    return this.initialize$;
  }

  getFeatureFlags(): Observable<FeatureFlag[]> {
    return this.http
      .get<TJsonApiBody>(`${this.urlV3}/flipper_gates`)
      .pipe(map((response) => this.jsona.deserialize({ ...response }) as FeatureFlag[]));
  }

  getFeatureFlag(featureName: string, accountID?: string): Observable<FeatureFlagEnabled> {
    const account = accountID ? `&account_id=${accountID}` : '';
    return this.http.get<FeatureFlagEnabled>(`${this.urlV4}/feature_flags?feature_flag=${featureName}${account}`);
  }

  async featureDisabled(featureName: string): Promise<boolean> {
    return !this.featureEnabled(featureName);
  }

  async featureEnabled(featureName: string): Promise<boolean> {
    await firstValueFrom(this.initialize());
    return this._featureFlags.get(featureName) ?? false;
  }
}
